/**
 * Body handling for both directions.
 *
 * `sanitiseOutgoingHtml` is not the defence against hostile inbound mail — received
 * HTML is rendered inside a sandboxed iframe whose content-security policy is the real
 * boundary. The sandbox stops script executing; the policy is what stops the sender's
 * network being reached at all, because a sandbox without `allow-scripts` still fetches
 * the subresources it will not run. `sanitiseOutgoingHtml` trims the body our own editor
 * produces so we never emit script, remote-loading or event-handler markup, and so a
 * paste from a web page cannot smuggle any in.
 *
 * The `cid` and remote-image helpers read received bodies, deciding what that policy
 * admits: parts of the message itself, never the sender's network.
 */

const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'div',
  'span',
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'strike',
  'a',
  'ul',
  'ol',
  'li',
  'blockquote',
  'pre',
  'code',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'table',
  'thead',
  'tbody',
  'tr',
  'td',
  'th',
  'img',
]);

const ALLOWED_ATTRS = new Set(['href', 'title', 'alt', 'src', 'width', 'height']);

const DROP_WITH_CONTENT = ['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math'];

const SAFE_URL = /^(https?:|mailto:|cid:|#|\/|[^:]*$)/i;
const SAFE_IMAGE_SRC = /^(https?:|cid:|data:image\/(png|jpeg|gif|webp);base64,)/i;

const ATTR_PATTERN = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*("[^"]*"|'[^']*'|[^\s"'>]+)/g;

function unquote(value: string): string {
  const first = value[0];
  return (first === '"' || first === "'") && value.endsWith(first) ? value.slice(1, -1) : value;
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

function sanitiseAttributes(tag: string, raw: string): string {
  const kept: string[] = [];
  for (const match of raw.matchAll(ATTR_PATTERN)) {
    const name = (match[1] ?? '').toLowerCase();
    const value = unquote(match[2] ?? '').trim();
    if (name.startsWith('on')) continue;
    if (!ALLOWED_ATTRS.has(name)) continue;
    if (name === 'href' && !SAFE_URL.test(value)) continue;
    if (name === 'src' && !SAFE_IMAGE_SRC.test(value)) continue;
    kept.push(`${name}="${escapeAttr(value)}"`);
  }
  if (tag === 'a' && kept.some((a) => a.startsWith('href='))) {
    kept.push('target="_blank"', 'rel="noopener noreferrer"');
  }
  return kept.length > 0 ? ` ${kept.join(' ')}` : '';
}

export function sanitiseOutgoingHtml(input: string): string {
  let html = input.replace(/<!--[\s\S]*?-->/g, '');

  for (const tag of DROP_WITH_CONTENT) {
    html = html.replace(new RegExp(`<${tag}\\b[\\s\\S]*?(?:</${tag}\\s*>|$)`, 'gi'), '');
  }

  return html.replace(
    /<\s*(\/)?\s*([a-zA-Z][a-zA-Z0-9-]*)((?:"[^"]*"|'[^']*'|[^>])*?)\/?\s*>/g,
    (_full, closing: string | undefined, rawTag: string, rawAttrs: string) => {
      const tag = rawTag.toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) return '';
      if (closing !== undefined) return `</${tag}>`;
      const attrs = sanitiseAttributes(tag, rawAttrs);
      return tag === 'br' || tag === 'hr' || tag === 'img'
        ? `<${tag}${attrs} />`
        : `<${tag}${attrs}>`;
    },
  );
}

// A `cid:` URL in received mail, in any attribute value or CSS url(). The delimiter is
// captured so the replacement can keep the quoting the sender used.
const CID_URL = /(["'(=])\s*cid:([^"')\s>]+)/gi;

function decodeCid(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/** Content-IDs the body references, so their parts are shown inline and not listed. */
export function cidReferences(html: string): string[] {
  const found = new Set<string>();
  for (const match of html.matchAll(CID_URL)) found.add(decodeCid(match[2] ?? ''));
  return [...found];
}

/**
 * Points every `cid:` reference at the part it names. `resolve` returns a URL, and one
 * it cannot resolve is left alone rather than pointed somewhere else.
 */
export function resolveCidImages(
  html: string,
  resolve: (cid: string) => string | undefined,
): string {
  return html.replace(CID_URL, (whole, delimiter: string, raw: string) => {
    const url = resolve(decodeCid(raw));
    return url === undefined ? whole : `${delimiter}${escapeAttr(url)}`;
  });
}

const IMG_TAG = /<img\b[^>]*>/gi;
const SRC_ATTR = /\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;

/** True when displaying the body would fetch something from the sender's network. */
export function hasRemoteImages(html: string): boolean {
  for (const tag of html.match(IMG_TAG) ?? []) {
    const match = SRC_ATTR.exec(tag);
    if (match === null) continue;
    const value = (match[1] ?? match[2] ?? match[3] ?? '').trim();
    if (/^https?:/i.test(value)) return true;
  }
  return /url\(\s*['"]?\s*https?:/i.test(html);
}

/** Plain-text alternative, so every message we send is multipart-capable. */
export function htmlToText(html: string): string {
  return sanitiseOutgoingHtml(html)
    .replace(/<\s*(br|\/p|\/div|\/li|\/h[1-6]|hr)\s*\/?\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** First line of a message body, for the thread list. */
export function previewOf(html: string, length = 140): string {
  const text = htmlToText(html).replace(/\s+/g, ' ');
  return text.length <= length ? text : `${text.slice(0, length - 1).trimEnd()}…`;
}
